import { Button } from "@/components/ui/button";
import { useHandleError } from "@/hooks/useHandleError";
import { useLineBrowserRedirect } from "@/hooks/useLineBrowserRedirect";
import { useGoogleLogin } from "@react-oauth/google";
import { useLoginWithGoogle } from "@roman/shared/hooks/auth/useLoginWithGoogle";
import { useAuthStore } from "@roman/shared/store/auth.store";
import type { GoogleLoginCallbackArgs } from "@roman/shared/types/Google";
import { getPlatform } from "@roman/shared/utils/platform";
import { toast } from "sonner";

export const GoogleLoginButton = ({ text }: { text: string }) => {
  const platform = getPlatform();
  const isPC = platform === "pc";
  const { trigger: triggerLoginWithGoogle } = useLoginWithGoogle(useHandleError);
  const loginWithGoogle = useAuthStore((state) => state.loginWithGoogle);

  // useGoogleLogin can only be called inside a component with callback functions
  const handleLogin = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      const code = codeResponse.code;
      const trigger = async ({ onSuccess, onError }: GoogleLoginCallbackArgs) => {
        await triggerLoginWithGoogle(
          { code },
          {
            onSuccess,
            onError,
          },
        );
      };

      await loginWithGoogle(trigger, toast);
    },
    onError: () => {
      toast.error("Login failed");
    },
    flow: "auth-code",
    scope: "email profile",
  });

  useLineBrowserRedirect();

  return (
    <Button
      variant="outline"
      onClick={handleLogin}
      className={`flex items-center w-[350px] px-4 py-6 rounded-full border-[#ADADAD] transition-all ease-out active:scale-95 active:shadow-sm ${isPC ? "hover:scale-105 hover:shadow-md hover:bg-white" : ""}`}
    >
      <img src="https://www.google.com/favicon.ico" alt="Google" className="w-[24px] h-[24px] mr-auto" />
      <span className="font-bold flex-grow text-center text-base text-[#2D2D2D]">{text}</span>
      <div className="w-5" />
    </Button>
  );
};
